import { ComponentType, lazy, LazyExoticComponent } from 'react'

interface LazyWithRetryOptions {
    /**
     * Number of retry attempts for failed chunk loads
     * @default 3
     */
    retryAttempts?: number

    /**
     * Delay in milliseconds between retry attempts
     * @default 1000
     */
    retryDelay?: number
}

/**
 * Workaround for React 18 production bug where Object.freeze on lazy
 * component internals causes "Cannot assign to read only property '_status'"
 * when concurrent re-renders are triggered by external state updates
 * (e.g., Amplify DataStore subscriptions).
 *
 * We patch Object.freeze once to skip freezing React lazy payload objects
 * (identified by having `_status` and `_result` properties).
 */
const originalFreeze = Object.freeze
let freezePatched = false

function patchObjectFreeze() {
    if (freezePatched) return
    freezePatched = true

    Object.freeze = function <T>(obj: T): T {
        // Skip freezing React lazy payload objects to prevent the _status bug.
        // These objects have numeric _status and _result properties used by
        // React's lazy component resolution.
        if (
            obj !== null &&
            typeof obj === 'object' &&
            '_status' in obj &&
            '_result' in obj
        ) {
            return obj
        }
        return originalFreeze(obj)
    } as typeof Object.freeze
}

// Apply the patch immediately on module load
patchObjectFreeze()

/**
 * Enhanced React.lazy that retries failed chunk loads
 *
 * When code splitting fails (e.g., due to network issues or stale chunks after deployment),
 * this utility will retry loading the chunk with exponential backoff.
 *
 * @param importFn - Dynamic import function that returns a promise resolving to a module with a default export
 * @param options - Configuration options for retry behavior
 * @returns LazyExoticComponent that can be used with React.Suspense
 *
 * @example
 * ```tsx
 * const Dashboard = lazyWithRetry(() => import('./routes/Dashboard'));
 *
 * function App() {
 *   return (
 *     <Suspense fallback={<LoadingSpinner />}>
 *       <Dashboard />
 *     </Suspense>
 *   );
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same constraint as React.lazy, so the component's own props are kept
export function lazyWithRetry<T extends ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    options: LazyWithRetryOptions = {}
): LazyExoticComponent<T> {
    const { retryAttempts = 3, retryDelay = 1000 } = options

    return lazy(() => {
        const retryImport = (
            attemptsLeft: number,
            attempt: number
        ): Promise<{ default: T }> => {
            return importFn().catch((error) => {
                if (attemptsLeft <= 0) {
                    throw error
                }

                // Log retry attempt
                console.warn(
                    `Failed to load chunk. Retrying... (${attempt}/${retryAttempts})`,
                    error
                )

                // Retry after delay with exponential backoff
                const delay = retryDelay * Math.pow(2, attempt - 1)
                return new Promise<{ default: T }>((resolve) => {
                    setTimeout(() => {
                        resolve(retryImport(attemptsLeft - 1, attempt + 1))
                    }, delay)
                })
            })
        }

        return retryImport(retryAttempts, 1)
    })
}
